import { Select, SelectVariantEnum } from "../../components/core/Select/Select";
import { TextSwitch } from "../../components/TextSwitch/TextSwitch";
import { RoundedContainer } from "../../components/RoundedContainer/RoundedContainer";
import {
  ALL_COLLABORATORS_LABEL,
  CONFIRM_CALIBRATION_FINAL,
  CONFIRM_CALIBRATION_FINAL_MESSAGE,
  DOWNLOAD_LABEL,
  NO_CONFIG_LABEL,
  POTENTIAL_MATRIX_LABEL,
  PRACTICE_LABEL,
  START_CALIBRATION_LABEL,
} from "../../constants/Dictionary";
import {
  StyledInlineSearchPanel,
  StyledMatrixFilters,
  StyledMessageContainer,
  StyledPotentialMatrix,
} from "./PotentialMatrix.styled";
import { useEffect, useRef, useState } from "react";
import { MatrixItem } from "../../types/api/potentialMatrix.type";
import { PermissionsEnum } from "../../constants/AppConstants";
import { IconSizeEnum, PageNames, Role } from "../../types/AppTypes";
import { Loader } from "../../components/core/Loader/Loader";
import { roleHasPermission } from "../../utils/roleAuth.utils";
import { useAppSelector } from "../../hooks/store.hook";
import { usePotentialMatrixFilters } from "../../hooks/usePotentialMatrixFilters.hook";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../components/core/Button/Button";
import { ReactComponent as CalibrationIcon } from "../../icons/calibration.svg";
import { ReactComponent as DownloadIcon } from "../../icons/download.svg";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import { ReactComponent as SearchActiveIcon } from "../../icons/searchActive.svg";
import { InputSizes } from "../../components/core/Input/Input";
import { CalibrationModal } from "../../components/CalibrationModal/CalibrationModal";
import generatePDF from "react-to-pdf";

import { PotentialMatrixFilterKeyEnum } from "../../types/api/filters.type";
import MatrixBox from "./MatrixBox/MatrixBox";
import {
  MatrixSizes,
  TypeMatrixEnum,
  typeOptions,
} from "../../constants/PotentialMatrixConstants";
import { ResumePanel } from "../../components/ResumePanel/ResumePanel";
import { MessageBox } from "../../components/MessageBox/MessageBox";
import { PopupConfirm } from "../../components/PopupConfirm/PopupConfirm";
import MatrixForDownload from "./MatrixForDownload/MatrixForDownload";
import { IconManager } from "../../components/core/IconManager/IconManager";
import { useDimensions } from "../../hooks/useDimensions.hook";
import { theme } from "../../constants/StyledComponents";
import { SearchCollaborators } from "../../components/SearchCollaborators/SearchCollaborators";
import {
  usePotentialMatrixDownload,
  usePotentialMatrix,
  usePotentialMatrixStats,
  useMatrixCollaborators,
} from "../../hooks/potentialMatrix.hook";
import { Matrix } from "../../components/Matrix/Matrix";

export enum MatrixViewMode {
  GLOBAL = "global",
  INTERNAL_GRADE = "internalGrade",
  COLLABORATOR = "collaborator",
  DOWNLOAD = "download",
}

export const PotentialMatrix = () => {
  const { profile } = useAppSelector((state) => state.user);
  const { width } = useDimensions();
  const { config, orgSettings } = useAppSelector((state) => state.config);
  const [searchPracticeValue, setSearchPracticeValue] = useState("");
  const [selectedViewMode, setSelectedViewMode] = useState<MatrixViewMode>(
    MatrixViewMode.GLOBAL,
  );
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [collaboratorId, setCollaboratorId] = useState<number | null>(null);
  const [isOpenConfirmCalibrationModal, setIsOpenConfirmCalibrationModal] =
    useState(false);
  const matrixRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadData, setDownloadData] = useState<MatrixItem[]>([]);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const yearOptions =
    config
      ?.map((item) => ({
        value: item.year,
        label: item.year.toString(),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 2) || [];

  const currentYear = yearOptions[0]?.value;
  const previousYear = yearOptions[1]?.value;

  const { filters, handleFilters, createQueryParams } =
    usePotentialMatrixFilters({
      currentYear,
      previousYear,
      internalGrade: ["all"],
    });

  const [itemDetails, setItemDetails] = useState<MatrixItem | null>(null);

  const configExists = config && config.length > 0;
  const { data, isLoading, isFetching } = usePotentialMatrix({
    createQueryParams: () => createQueryParams(9),
    filters,
    enabled: itemDetails === null && configExists,
  });

  const {
    data: potentialMatrixStatsData,
    isFetching: loadingPotentialMatrixStats,
  } = usePotentialMatrixStats({
    filters,
    enabled: configExists,
  });

  const { mutateAsync: fetchDownloadData } = usePotentialMatrixDownload({
    filters,
  });

  const {
    data: matrixCollaborators,
    initLoading: matrixCollaboratorsLoading,
    canLoadMore: canLoadMoreCollaborators,
    fetchMore: fetchMoreCollaborators,
    fetchingMore: fetchingMoreCollaborators,
    clearSearch: clearMatrixCollaboratorsSearch,
    setSearch: setMatrixCollaboratorsSearchValue,
  } = useMatrixCollaborators({
    filters,
    enabled: showSearch && searchValue !== "",
  });

  useBreadCrumb([
    { name: POTENTIAL_MATRIX_LABEL, pathName: PageNames.POTENTIAL_MATRIX },
  ]);

  const practiceList = orgSettings.areaList.filtered.map(
    (item) => item.practiceList,
  )[0];

  const filteredPracticeOptions =
    searchPracticeValue && practiceList
      ? practiceList.filter((option) =>
          option.name.toLowerCase().includes(searchPracticeValue.toLowerCase()),
        )
      : practiceList || [];

  const handleDownloadMatrix = async () => {
    if (!matrixRef.current || itemDetails) return;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    await generatePDF(() => matrixRef.current, {
      filename: `potential-matrix-${filters.year}.pdf`,
      method: "save",
      page: {
        margin: 6,
        format: "a4",
        orientation: "landscape",
      },
      canvas: {
        mimeType: "image/png",
        qualityRatio: 1,
        useCORS: true,
      },
      overrides: {
        pdf: {
          compress: true,
        },
        canvas: {
          useCORS: true,
        },
      },
    });
  };

  const loadDownload = async () => {
    try {
      setIsDownloading(true);
      const downloadLimit =
        data?.reduce((max, item) => Math.max(max, item.count), 0) ?? 0;
      const result = await fetchDownloadData(downloadLimit);
      setDownloadData(result);
      await handleDownloadMatrix();
    } finally {
      setIsDownloading(false);
    }
  };

  const isDownloadEnabled = () => {
    if (itemDetails) {
      return false;
    }

    if (isLoading) {
      return false;
    }

    if (filters.practice.length === 1) {
      return true;
    }

    if (
      [Role.P_LEADER, Role.CC_LEADER, Role.CC_MANAGER].includes(profile.role)
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (selectedViewMode !== MatrixViewMode.COLLABORATOR) {
      setShowSearch(false);
      setSearchValue("");
      clearMatrixCollaboratorsSearch();
      handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    }
  }, [selectedViewMode]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setMatrixCollaboratorsSearchValue(value);

    if (filters.collaboratorId) {
      handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    }
  };

  const handleSelectCollaborator = (
    collaboratorId: number,
    collaboratorLabel: string,
  ) => {
    setSearchValue(collaboratorLabel);
    setMatrixCollaboratorsSearchValue(collaboratorLabel);
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, collaboratorId);
    setShowSearch(false);
  };

  const handleClearSearch = () => {
    handleFilters(PotentialMatrixFilterKeyEnum.COLLABORATOR_ID, null);
    setSearchValue("");
    clearMatrixCollaboratorsSearch();
    setShowSearch(false);
  };

  const toggleSearchInput = () => {
    setShowSearch(false);
    if (filters.collaboratorId === null) {
      setSearchValue("");
      clearMatrixCollaboratorsSearch();
    }
  };

  return (
    <RoundedContainer fullHeight>
      {configExists ? (
        <StyledPotentialMatrix>
          <StyledMatrixFilters>
            {roleHasPermission(
              profile,
              PermissionsEnum.VIEW_MATRIX_PRACTICE_SELECT,
            ) ? (
              <div className="select-practice-container">
                <Select
                  variant={SelectVariantEnum.MULTI_SELECT}
                  label={PRACTICE_LABEL}
                  options={filteredPracticeOptions.map((items) => ({
                    value: items.id,
                    label: items.name,
                  }))}
                  value={filters.practice ?? ""}
                  onChange={(value) => {
                    handleFilters(
                      PotentialMatrixFilterKeyEnum.PRACTICE,
                      value as string[],
                    );
                  }}
                  withSearch
                  onSearch={setSearchPracticeValue}
                  searchValue={searchPracticeValue}
                />
              </div>
            ) : (
              <div />
            )}
            <div className="switch-container">
              <TextSwitch
                options={
                  filters.year === previousYear ? [typeOptions[1]] : typeOptions
                }
                selectedOption={filters.type}
                setSelectedOption={(typeOption) => {
                  handleFilters(PotentialMatrixFilterKeyEnum.TYPE, typeOption);
                }}
              />
            </div>
            <div className="filters-end-container">
              <div className="select-year-container">
                <Select
                  variant={SelectVariantEnum.DEFAULT}
                  onChange={(yearOption) => {
                    handleFilters(
                      PotentialMatrixFilterKeyEnum.YEAR,
                      yearOption as number,
                    );
                  }}
                  size={InputSizes.SMALL}
                  options={yearOptions}
                  value={filters.year}
                  allowClear={false}
                />
              </div>
              {isDownloading ? (
                <Loader loading={isDownloading} size={20} />
              ) : (
                filters.type.value === TypeMatrixEnum.FINAL_EVALUATION && (
                  <Button
                    label={
                      width > theme.breakpoints.XL ? DOWNLOAD_LABEL : undefined
                    }
                    variant={ButtonVariants.PRIMARY}
                    size={ButtonSizes.SMALL}
                    icon={<DownloadIcon />}
                    disabled={!isDownloadEnabled()}
                    onClick={loadDownload}
                  />
                )
              )}
              {selectedViewMode === MatrixViewMode.COLLABORATOR &&
                itemDetails === null && (
                  <div className="search-container">
                    <IconManager
                      onClick={() => setShowSearch(true)}
                      size={IconSizeEnum.MEDIUM}
                    >
                      {filters.collaboratorId ? (
                        <SearchActiveIcon />
                      ) : (
                        <SearchIcon />
                      )}
                    </IconManager>
                    <StyledInlineSearchPanel $visible={showSearch}>
                      <SearchCollaborators
                        value={searchValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        onSelectCollaborator={handleSelectCollaborator}
                        data={matrixCollaborators?.map(
                          (item) => item.collaborator,
                        )}
                        loading={matrixCollaboratorsLoading}
                        fetchingMore={fetchingMoreCollaborators}
                        canLoadMore={canLoadMoreCollaborators}
                        fetchMore={fetchMoreCollaborators}
                        showEmptyMessage={!filters.collaboratorId}
                        toggleSearchInput={toggleSearchInput}
                      />
                    </StyledInlineSearchPanel>
                  </div>
                )}
            </div>
          </StyledMatrixFilters>
          <MatrixBox
            selectedViewMode={selectedViewMode}
            setSelectedViewMode={setSelectedViewMode}
            data={data ?? []}
            loading={isFetching}
            filters={filters}
            onClickMatrixItem={(item) => {
              setItemDetails(item);
            }}
            onCloseDetails={() => setItemDetails(null)}
            handleFilters={handleFilters}
            {...(itemDetails && { matrixItem: itemDetails })}
            onClickResume={(collaborator) => {
              setShowResume(true);
              setCollaboratorId(collaborator.id);
            }}
            potentialMatrixStatsData={potentialMatrixStatsData ?? []}
            loadingPotentialMatrixStats={loadingPotentialMatrixStats}
            showDetailsChip={!filters.collaboratorId}
          />
          {isDownloadEnabled() && (
            <MatrixForDownload
              ref={matrixRef}
              year={filters.year}
              title="Matrice del Potenziale - Calibration finale"
              subtitleItems={[
                {
                  label: PRACTICE_LABEL,
                  value: `${filters.practice[0]} | ${ALL_COLLABORATORS_LABEL}`,
                },
              ]}
            >
              <Matrix
                yearSelected={filters.year}
                data={downloadData ?? []}
                size={MatrixSizes.BIG}
                viewMode={MatrixViewMode.DOWNLOAD}
              />
            </MatrixForDownload>
          )}
          {roleHasPermission(
            profile,
            PermissionsEnum.CAN_START_CALIBRATION,
          ) && (
            <div className="footer">
              <Button
                label={START_CALIBRATION_LABEL}
                variant={ButtonVariants.PRIMARY}
                size={ButtonSizes.REGULAR}
                icon={<CalibrationIcon />}
                onClick={() => setShowCalibrationModal(true)}
              />
            </div>
          )}
          {showCalibrationModal && (
            <>
              <CalibrationModal
                isOpen
                closeModal={() => setIsOpenConfirmCalibrationModal(true)}
                year={currentYear}
              />
              <PopupConfirm
                isOpen={isOpenConfirmCalibrationModal}
                onCancel={() => setIsOpenConfirmCalibrationModal(false)}
                onConfirm={() => {
                  setIsOpenConfirmCalibrationModal(false);
                  setShowCalibrationModal(false);
                }}
                label={CONFIRM_CALIBRATION_FINAL}
                isLoading={isLoading}
                popupText={CONFIRM_CALIBRATION_FINAL_MESSAGE}
              />
            </>
          )}
        </StyledPotentialMatrix>
      ) : (
        <StyledMessageContainer>
          <MessageBox message={NO_CONFIG_LABEL} />
        </StyledMessageContainer>
      )}
      <ResumePanel
        collaboratorId={collaboratorId}
        isOpen={showResume}
        onClose={() => {
          setShowResume(false);
          setCollaboratorId(null);
        }}
        fullHeight
        readOnly
        hideEditButton
      />
    </RoundedContainer>
  );
};
